import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertCircle, LogIn, TrendingUp } from 'lucide-react';
import { MOTIVOS_ENTRADA_BODEGA } from '@suminuta/shared';
import type { MotivoEntradaBodega } from '@suminuta/shared';
import { obtenerFechaActualLocal } from '../utils/dateUtils';
import type { Ingrediente } from '../types/ingredientes';
import type { EntradaBodegaPayload } from '../services/ingredientesService';

/**
 * Registro manual de una entrada de bodega (PROMPT_N°052).
 *
 * El espejo de `ModalSalidaBodega`. Cubre lo que entra sin documento:
 * devolución desde cocina, compra menor con boleta, diferencia de
 * inventario físico. Lo que entra por OC o por factura tiene su propio
 * circuito y no pasa por acá.
 *
 * El costo viene precargado con la referencia del catálogo y es opcional:
 * una devolución desde cocina no tiene precio nuevo, y obligar a inventarlo
 * sería peor que heredar el que ya está. **Lo que se escriba acá no cambia
 * la referencia del ingrediente** — sólo el costo de este lote.
 */

interface ModalEntradaBodegaProps {
  isOpen: boolean;
  onClose: () => void;
  ingrediente: Ingrediente | null;
  onConfirm: (data: EntradaBodegaPayload) => void;
  isLoading?: boolean;
}

const MOTIVOS = Object.entries(MOTIVOS_ENTRADA_BODEGA) as Array<
  [MotivoEntradaBodega, string]
>;

const ModalEntradaBodega: React.FC<ModalEntradaBodegaProps> = ({
  isOpen,
  onClose,
  ingrediente,
  onConfirm,
  isLoading = false,
}) => {
  const [cantidad, setCantidad] = useState<string>('');
  const [motivo, setMotivo] = useState<MotivoEntradaBodega>('devolucion_cocina');
  const [origen, setOrigen] = useState<string>('');
  const [fecha, setFecha] = useState<string>(obtenerFechaActualLocal());
  const [vencimiento, setVencimiento] = useState<string>('');
  const [costo, setCosto] = useState<string>('');
  const [notas, setNotas] = useState<string>('');

  const costoReferencia = ingrediente?.costoUnitario ?? 0;

  useEffect(() => {
    if (isOpen && ingrediente) {
      setCantidad('');
      setMotivo('devolucion_cocina');
      setOrigen('');
      setFecha(obtenerFechaActualLocal());
      setVencimiento('');
      setCosto(String(ingrediente.costoUnitario ?? 0));
      setNotas('');
    }
  }, [isOpen, ingrediente]);

  const stockActual = ingrediente?.stockActual ?? 0;
  const unidad = ingrediente?.unidadMedida?.abreviacion ?? '';
  const cantidadNum = parseFloat(cantidad);
  const costoNum = parseFloat(costo);

  const errorCantidad = useMemo(() => {
    if (cantidad.trim() === '') return null;
    if (isNaN(cantidadNum)) return 'Ingresá un número válido';
    if (cantidadNum <= 0) return 'La cantidad debe ser mayor a 0';
    return null;
  }, [cantidad, cantidadNum]);

  const errorCosto = useMemo(() => {
    if (costo.trim() === '') return null;
    if (isNaN(costoNum)) return 'Ingresá un número válido';
    if (costoNum < 0) return 'El costo no puede ser negativo';
    return null;
  }, [costo, costoNum]);

  const puedeGuardar =
    !isLoading &&
    cantidad.trim() !== '' &&
    !errorCantidad &&
    !errorCosto &&
    notas.length <= 500 &&
    origen.length <= 100;

  if (!isOpen || !ingrediente) return null;

  const stockResultante = stockActual + (cantidadNum || 0);
  const costoCambiado =
    costo.trim() !== '' && !isNaN(costoNum) && costoNum !== costoReferencia;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeGuardar) return;

    onConfirm({
      cantidad: cantidadNum,
      motivo,
      fecha,
      origen: origen.trim() || undefined,
      // Sólo se manda si difiere de la referencia: así el backend sabe que
      // fue una decisión y no un valor arrastrado por el formulario.
      costo_unitario: costoCambiado ? costoNum : undefined,
      fecha_vencimiento: vencimiento || undefined,
      notas: notas.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 p-2">
              <LogIn className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Registrar entrada de bodega
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {ingrediente.nombre} —{' '}
                <span className="font-medium">
                  Stock actual: {stockActual} {unidad}
                </span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            disabled={isLoading}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Cantidad */}
          <div>
            <label
              htmlFor="entrada-cantidad"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Cantidad que entra *
            </label>
            <div className="relative">
              <input
                type="number"
                id="entrada-cantidad"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                className={`w-full px-3 py-2 pr-16 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  errorCantidad
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={`Cantidad en ${unidad}`}
                step="0.01"
                min="0"
                disabled={isLoading}
                autoFocus
              />
              <div className="absolute right-3 top-2 text-gray-500 dark:text-gray-400">
                {unidad}
              </div>
            </div>
            {errorCantidad && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle size={14} />
                {errorCantidad}
              </p>
            )}
            {!errorCantidad && cantidadNum > 0 && (
              <div className="mt-3 p-3 rounded-lg flex items-center gap-2 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                <TrendingUp size={20} />
                <div className="text-sm">
                  Quedan{' '}
                  <span className="font-semibold">
                    {stockResultante.toFixed(2)} {unidad}
                  </span>{' '}
                  después de esta entrada.
                </div>
              </div>
            )}
          </div>

          {/* Motivo + Origen */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="entrada-motivo"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Motivo *
              </label>
              <select
                id="entrada-motivo"
                value={motivo}
                onChange={(e) =>
                  setMotivo(e.target.value as MotivoEntradaBodega)
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                disabled={isLoading}
              >
                {MOTIVOS.map(([valor, etiqueta]) => (
                  <option key={valor} value={valor}>
                    {etiqueta}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="entrada-origen"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Origen
              </label>
              <input
                type="text"
                id="entrada-origen"
                value={origen}
                onChange={(e) => setOrigen(e.target.value)}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Cocina, Líder, conteo físico…"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="entrada-fecha"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Fecha de la entrada
              </label>
              <input
                type="date"
                id="entrada-fecha"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Por defecto hoy.
              </p>
            </div>

            <div>
              <label
                htmlFor="entrada-vencimiento"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Vence (opcional)
              </label>
              <input
                type="date"
                id="entrada-vencimiento"
                value={vencimiento}
                onChange={(e) => setVencimiento(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Si lo cargás, el lote entra en la cola FEFO por esa fecha.
              </p>
            </div>
          </div>

          {/* Costo */}
          <div>
            <label
              htmlFor="entrada-costo"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Costo unitario
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">
                $
              </span>
              <input
                type="number"
                id="entrada-costo"
                value={costo}
                onChange={(e) => setCosto(e.target.value)}
                className={`w-full pl-7 pr-20 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  errorCosto
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                step="0.01"
                min="0"
                disabled={isLoading}
              />
              <div className="absolute right-3 top-2 text-xs text-gray-500 dark:text-gray-400">
                por {unidad}
              </div>
            </div>
            {errorCosto ? (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle size={14} />
                {errorCosto}
              </p>
            ) : (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Si no lo sabés, dejá el de referencia (${costoReferencia} por{' '}
                {unidad}). Lo que pongas acá es el costo de <em>este lote</em>:
                no cambia el costo de referencia del ingrediente.
              </p>
            )}
          </div>

          {/* Notas */}
          <div>
            <label
              htmlFor="entrada-notas"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Notas (opcional)
            </label>
            <textarea
              id="entrada-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Nº de boleta, quién lo trajo, etc."
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {notas.length}/500 caracteres
            </p>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={!puedeGuardar}
            >
              {isLoading ? 'Registrando…' : 'Registrar entrada'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEntradaBodega;
